export interface TourStep {
  element: string; // CSS selector for the element to highlight
  title: string;
  description: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
}

export interface ChangelogLink {
  label: string;
  url: string; // Internal route path
}

export interface ChangelogEntry {
  version: string; // Must match appVersion.ts for the latest
  date: string; // YYYY-MM-DD
  title: string;
  description?: string;
  highlights: string[];
  sparkles?: boolean; // Optional: give a special release a celebratory sparkle treatment
  badge?: string; // Optional: label for a special animated badge (major releases)
  tourSteps?: TourStep[]; // Optional: guided tour for this version
  tourRoute?: string; // Optional: navigate here before starting tour
  links?: ChangelogLink[]; // Optional: quick-links to new features
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '2.1.0',
    date: '2026-08-03',
    title: 'MCP is here',
    sparkles: true,
    badge: 'New Integration',
    description: 'Use your favorite AI with Ticketin to build forms easily. MCP is a secure connection that lets your AI assistant use only the Ticketin tools and content you approve — no coding needed.',
    highlights: [
      'Create and update Ticketin forms by simply describing what you need to ChatGPT or Claude',
      'Follow clear illustrated setup guides for both ChatGPT and Claude directly from Settings',
      'Developers can connect Claude Code or Codex with copyable setup commands and official documentation links',
      'Sign in with your normal Ticketin account and choose exactly which organizations and forms your AI may access',
      'Stay in control of destructive actions — deleting a form always requires a clear warning and your confirmation',
    ],
    links: [
      { label: 'Set up AI connections', url: '/settings' },
    ],
  },
  {
    version: '2.0.0',
    date: '2026-06-18',
    title: 'A Fresh New Look',
    badge: 'Major Update',
    highlights: [
      'A completely fresh new UI — the whole app has been redesigned to feel more modern, friendly, and consistent, with a polished light and dark mode throughout',
    ],
  },
  {
    version: '0.1.41',
    date: '2026-04-26',
    title: 'Payment Status View for Answers',
    highlights: [
      'New Payment Status view in the answers table — registrants who share the same payment link are grouped into a single card so families and group purchases stay together',
      'Each group is colored green "Paid" or red "Unpaid" at a glance, with the payment link, member count, and a copy button right in the header',
      'Approve, mark Waiting, or Deny an entire group with one click — no need to select members one by one',
      'Filter by All, Unpaid, or Paid (defaults to Unpaid so the groups that still need action are front-and-center)',
      'Skeleton placeholders while groups load or when you switch filters, so the screen no longer briefly shows stale data',
    ],
  },
  {
    version: '0.1.40',
    date: '2026-04-25',
    title: 'Ticket Type Counts on Sessions & Smoother Org Switching',
    highlights: [
      'Each session card now shows its ticket types with how many are still available — see "Early Bird: 120 left" or "Sold out" at a glance',
      'Switching between organizations no longer briefly shows the previous org\'s events — you\'ll see skeleton placeholders until the new org\'s data is ready',
    ],
  },
  {
    version: '0.1.39',
    date: '2026-04-23',
    title: 'Session Start Times & Ticket Type Waves',
    highlights: [
      'Session start date now supports a specific time (24-hour format) — schedule sessions to open at an exact hour',
      'The "Session Duration" range has been split into separate Start Date & Time and End Date pickers for clearer scheduling',
      'New Ticket Types (Waves) manager for paid sessions — add multiple tiers (e.g. Early Bird, VIP, Normal), each with its own name, quantity, and price',
      'Edit existing ticket types inline from the session page — update a tier\'s name, quantity, or price without leaving the form',
    ],
  },
  {
    version: '0.1.38',
    date: '2026-04-05',
    title: 'Smarter Survey Exports & Cleaner Survey View',
    highlights: [
      'Survey exports now include all data in a single Excel sheet — no more separate tabs for complex or conditional fields',
      'Personal columns (name, email, phone, ticket, etc.) are automatically hidden in survey mode for a cleaner, anonymous view',
      'Complex field subfields appear as inline columns in the main sheet when exporting surveys',
      'Conditional field answers and their subfields are flattened into the main export sheet for surveys',
    ],
  },
  {
    version: '0.1.37',
    date: '2026-04-01',
    title: 'Survey Mode for Sessions',
    highlights: [
      'New Survey Mode — mark any session as a survey instead of a regular ticketing session',
    ],
  },
  {
    version: '0.1.36',
    date: '2026-04-01',
    title: "Stay in the Loop with What's New",
    highlights: [
      'See what changed after every update — a popup now shows the latest improvements',
      'Take guided tours of new features with step-by-step walkthroughs',
      'Browse the full release history anytime from the top navigation',
      'Smoother updates — improved cache busting ensures you always get the latest version',
    ],
    links: [
      { label: "What's New", url: '/whats-new' },
    ],
  },
  {
    version: '0.1.35',
    date: '2026-03-31',
    title: 'Conditional Fields & Answer Display Improvements',
    highlights: [
      'Enhanced conditional field handling in form builder',
      'Improved answer table with better data display',
      'Date-based attendance filtering in answers',
    ],
    links: [
      { label: 'Form Builder', url: '/forms' },
    ],
  },
];
